import React, { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import TspApiService from "../../../api/tspApiService";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;
const TYPE_LABEL = { state: "State", district: "District", city: "Town" };
const UNAVAILABLE = "Location search is not available right now.";

// Search box for the coverage map: an Indian state, district or town by name.
//
// While typing, names come from GET /tsp/map/places (ICC's own state / LGD
// district and town lists — case-insensitive, partial, and by common alternate
// names such as Bangalore / Bombay, shown beside the standard name). Picking one (a click,
// Enter on the highlighted row, or Enter when exactly one place matches) asks
// GET /tsp/map/places/locate where it is, and `onLocate(place)` moves the map.
// Several matches are always listed to choose from — never an arbitrary pick —
// and a name that matches nothing, or a place that cannot be located, says
// "Location not found". The map's journey markers are never touched here.
// `onClear()` (optional) says the last found place no longer applies: the box was
// cleared, or another place is being looked up (so its pin never sits beside
// different text).
export default function CoverageLocationSearch({ onLocate, onClear }) {
  const [text, setText] = useState("");
  const [results, setResults] = useState(null); // null = nothing searched yet
  const [more, setMore] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [status, setStatus] = useState({ kind: "idle", message: "" }); // idle | searching | locating | error | info
  const searchSeq = useRef(0);
  const locateSeq = useRef(0);
  const enterPending = useRef(false);
  const pickedLabel = useRef(null); // the text a pick wrote into the box: not a new search
  const rootRef = useRef(null);

  const query = text.trim();

  // Names for the typed text (debounced; a late answer for older text is dropped).
  useEffect(() => {
    if (pickedLabel.current !== null && text === pickedLabel.current) return undefined;
    if (query.length < MIN_CHARS) {
      searchSeq.current += 1;
      setResults(null);
      setMore(false);
      setStatus((current) => (current.kind === "searching" ? { kind: "idle", message: "" } : current));
      return undefined;
    }
    const seq = ++searchSeq.current;
    setStatus({ kind: "searching", message: "" });
    const timer = setTimeout(() => {
      TspApiService.searchMapPlaces(query)
        .then((response) => {
          if (seq !== searchSeq.current) return;
          const places = response.data?.places || [];
          setResults(places);
          setMore(Boolean(response.data?.more));
          setActive(places.length ? 0 : -1);
          setStatus(places.length ? { kind: "idle", message: "" } : { kind: "error", message: "Location not found" });
          if (enterPending.current) {
            enterPending.current = false;
            if (places.length === 1) locate(places[0]);
            else setOpen(true);
          }
        })
        .catch(() => {
          if (seq !== searchSeq.current) return;
          enterPending.current = false;
          setResults([]);
          setStatus({ kind: "error", message: UNAVAILABLE });
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // A click outside closes the list.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  function locate(place) {
    const seq = ++locateSeq.current;
    setOpen(false);
    pickedLabel.current = place.label;
    setText(place.label);
    setStatus({ kind: "locating", message: `Finding ${place.label}…` });
    onClear?.();
    TspApiService.locateMapPlace({ type: place.type, id: place.id })
      .then((response) => {
        if (seq !== locateSeq.current) return;
        const located = response.data;
        onLocate(located);
        setStatus(
          located.approximate
            ? { kind: "info", message: `${place.name} town was not found on the map — showing ${located.district_name ? `${located.district_name} district` : "its district"} instead.` }
            : { kind: "idle", message: "" }
        );
      })
      .catch((error) => {
        if (seq !== locateSeq.current) return;
        const httpStatus = error?.response?.status;
        setStatus({ kind: "error", message: httpStatus === 404 ? "Location not found" : UNAVAILABLE });
      });
  }

  const showList = open && Array.isArray(results) && results.length > 0;

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown" && results && results.length) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index + 1) % results.length);
    } else if (event.key === "ArrowUp" && results && results.length) {
      event.preventDefault();
      setOpen(true);
      setActive((index) => (index <= 0 ? results.length - 1 : index - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (query.length < MIN_CHARS) return;
      if (status.kind === "searching") {
        enterPending.current = true; // decide once the names arrive
        return;
      }
      if (!results || results.length === 0) {
        setStatus({ kind: "error", message: "Location not found" });
      } else if (showList && active >= 0) {
        locate(results[active]);
      } else if (results.length === 1) {
        locate(results[0]);
      } else {
        setOpen(true); // several places match: let the person choose
      }
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const clear = () => {
    searchSeq.current += 1;
    locateSeq.current += 1;
    enterPending.current = false;
    pickedLabel.current = null;
    setText("");
    setResults(null);
    setOpen(false);
    setStatus({ kind: "idle", message: "" });
    onClear?.();
  };

  return (
    <div className="tsp-cov-search" ref={rootRef}>
      <div className="tsp-cov-search-box">
        <Search size={14} aria-hidden="true" className="tsp-cov-search-icon" />
        <input
          type="search"
          className="tsp-cov-search-input"
          placeholder="Search city, district or state"
          value={text}
          onChange={(event) => {
            pickedLabel.current = null;
            setText(event.target.value);
            setOpen(true);
            enterPending.current = false;
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-label="Search an Indian city, district or state"
          aria-expanded={showList}
          aria-controls="tsp-cov-search-list"
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? `tsp-cov-search-option-${active}` : undefined}
        />
        {text && (
          <button type="button" className="tsp-cov-search-clear" onClick={clear} aria-label="Clear location search">
            <X size={13} aria-hidden="true" />
          </button>
        )}
      </div>
      {showList && (
        <ul className="tsp-cov-search-list" id="tsp-cov-search-list" role="listbox" aria-label="Matching locations">
          {results.map((place, index) => (
            <li
              key={`${place.type}:${place.id}`}
              id={`tsp-cov-search-option-${index}`}
              role="option"
              aria-selected={index === active}
              className={`tsp-cov-search-option${index === active ? " is-active" : ""}`}
              onMouseDown={(event) => event.preventDefault()} // keep focus in the box
              onMouseEnter={() => setActive(index)}
              onClick={() => locate(place)}
            >
              <span className="tsp-cov-search-option-name">
                {place.label}
                {place.also_known_as && <span className="tsp-cov-search-option-aka"> · {place.also_known_as}</span>}
              </span>
              <span className="tsp-cov-search-option-type">{TYPE_LABEL[place.type]}</span>
            </li>
          ))}
          {more && <li className="tsp-cov-search-more">More places match — type more of the name.</li>}
        </ul>
      )}
      {status.message && (
        <p className={`tsp-cov-search-status tsp-cov-search-status--${status.kind}`} role={status.kind === "error" ? "alert" : "status"}>
          {status.message}
        </p>
      )}
    </div>
  );
}
